import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "@/lib/api-client";

/**
 * `host.useData` (ADMIN-261): a widget's content (a note's text, a task list), stored on the
 * server in `home_widget_data` and mirrored in localStorage.
 *
 * - **Instant**: the local copy paints first, then the server's copy replaces it if it's newer.
 * - **Saves** shortly after the last change, with the version it was based on (If-Match).
 * - **Conflicts**: content is one value, so there's nothing to merge; the latest typing wins and
 *   is saved again on top of the newer version.
 * - **Other tabs** follow the local copy (`storage` event); other devices on focus.
 * - **Restored content**: when a board save hands a removed widget's content to a new one, the
 *   board says so with {@link RESTORED_EVENT} and the widget loads it.
 */

const SAVE_DELAY_MS = 600;

/** Fired on `window` with the ids of widgets whose content the server just restored. */
export const RESTORED_EVENT = "kourai:widget-data-restored";

type Local<T> = { data: T; version: number; dirty?: boolean };

/** @internal exported for tests */
export const widgetDataKey = (workspaceId: string, owner: string, widgetId: string) =>
  `kourai-widget-data:${workspaceId}:${owner}:${widgetId}`;

/** @internal exported for tests */
export function parseLocal<T>(raw: string | null): Local<T> | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Local<T>;
    return v && typeof v === "object" && "data" in v && typeof v.version === "number" ? v : null;
  } catch {
    return null;
  }
}

function readLocal<T>(k: string): Local<T> | null {
  try {
    return parseLocal<T>(localStorage.getItem(k));
  } catch {
    return null;
  }
}

function writeLocal<T>(k: string, v: Local<T>) {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {
    // Storage full or blocked: the server copy is still saved.
  }
}

type Stored = { data: unknown; version: number };

export function useWidgetData<T>(
  p: { workspaceId: string; owner: string; widgetId: string; type: string; shared: boolean },
  initial: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const api = useApi();
  /** Only fills an empty widget, so a new `initial` each render doesn't refetch. */
  const initialRef = useRef(initial);
  const k = widgetDataKey(p.workspaceId, p.owner, p.widgetId);
  const start = (): Local<T> => readLocal<T>(k) ?? { data: initial, version: 0 };
  const [state, setState] = useState<Local<T>>(start);
  const ref = useRef(state);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saving = useRef(false);
  const shown = useRef(k);
  shown.current = k;

  const keep = useCallback(
    (next: Local<T>) => {
      ref.current = next;
      setState(next);
      writeLocal(k, next);
    },
    [k],
  );

  const endpoint = useCallback(() => {
    const route = api.api.workspaces[":id"].home.widgets[":widgetId"].data;
    const args = {
      param: { id: p.workspaceId, widgetId: p.widgetId },
      query: p.shared ? { scope: "workspace" } : {},
    };
    return { route, args };
  }, [api, p.workspaceId, p.widgetId, p.shared]);

  const saveRef = useRef<() => Promise<void>>(async () => {});
  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      void saveRef.current();
    }, SAVE_DELAY_MS);
  }, []);

  const save = useCallback(async () => {
    const current = ref.current;
    if (!current.dirty || saving.current) return;
    saving.current = true;
    const myKey = k;
    try {
      const { route, args } = endpoint();
      const res = await route.$put(
        { ...args, json: { type: p.type, data: current.data } } as never,
        { headers: { "If-Match": `"${current.version}"` } },
      );
      const body = (await res.json()) as { version?: number; current?: Stored };
      saving.current = false;
      if (shown.current !== myKey) return;
      const latest = ref.current;
      if (res.ok && typeof body.version === "number") {
        const dirty = latest.data !== current.data;
        keep({ ...latest, version: body.version, dirty });
        if (dirty) schedule();
      } else if (res.status === 409 && body.current) {
        // Someone saved in between: this device's latest typing goes on top.
        keep({ ...latest, version: body.current.version, dirty: true });
        schedule();
      }
    } catch {
      // Offline: stays dirty locally and goes out on the next change or focus.
    } finally {
      saving.current = false;
    }
  }, [k, endpoint, p.type, keep, schedule]);
  saveRef.current = save;

  const refresh = useCallback(async () => {
    const myKey = k;
    try {
      const { route, args } = endpoint();
      const res = await route.$get(args as never);
      if (!res.ok || shown.current !== myKey) return;
      const stored = (await res.json()) as Stored;
      const current = ref.current;
      if (current.dirty) {
        // Unsaved edits here: they win, saved on top of whatever the server has.
        if (stored.version !== current.version) keep({ ...current, version: stored.version });
        if (!timer.current && !saving.current) schedule();
        return;
      }
      if (stored.version !== current.version)
        keep({ data: (stored.data ?? initialRef.current) as T, version: stored.version });
    } catch {
      // Offline: the local copy is what there is.
    }
  }, [k, endpoint, keep, schedule]);

  // A new widget (or workspace): its local copy at once, then the server's.
  // biome-ignore lint/correctness/useExhaustiveDependencies: start reads the new key
  useEffect(() => {
    const next = start();
    ref.current = next;
    setState(next);
    void refresh();
  }, [k, refresh]);

  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const onRestored = (e: Event) => {
      if ((e as CustomEvent<string[]>).detail?.includes(p.widgetId)) void refresh();
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key !== k || timer.current || saving.current) return;
      const next = parseLocal<T>(e.newValue);
      if (!next) return;
      ref.current = next;
      setState(next);
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener(RESTORED_EVENT, onRestored);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener(RESTORED_EVENT, onRestored);
      window.removeEventListener("storage", onStorage);
    };
  }, [k, p.widgetId, refresh]);

  // Leaving (widget removed, tab closing): send what's pending now rather than drop it.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
        void saveRef.current();
      }
    },
    [],
  );

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      const current = ref.current;
      const data =
        typeof next === "function" ? (next as (prev: T) => T)(current.data) : (next as T);
      if (data === current.data) return;
      keep({ ...current, data, dirty: true });
      schedule();
    },
    [keep, schedule],
  );

  return [state.data, set];
}
